#!/usr/bin/env node
/**
 * sibyl — command line entry.
 *
 * ⛔ Exit 0 means the run completed, INCLUDING a run that found eleven HIGH findings. Finding a
 * defect is a successful run. A crashed harness and a productive review must not exit the same way
 * — verdict's own worst documented defect was mapping a non-zero exit straight to "failed",
 * which told the user the harness broke when it had worked perfectly.
 */

import { createRequire } from "node:module"
import { loadConfig, describeResolution } from "../config/load.js"

const require = createRequire(import.meta.url)
import { ConfigError, EXIT, type Diagnostic } from "../config/validate.js"

/** Returned by a command that owns the process from here on, such as the server. */
export const STAY_ALIVE = -1

const USAGE = `sibyl — automated pull-request review, asked for from GitHub

  sibyl review <pr>          review a pull request; findings go to the local queue
  sibyl recheck <pr>         re-decide prior findings against the current head
  sibyl triage [--all]       show the triage queue
  sibyl triage accept <fp>   accept a finding
  sibyl triage dismiss <fp> --reason <r>   dismiss one, with a reason that is recorded
  sibyl post <pr> [--dry-run]  post the accepted findings as ONE review; refuses while a lock is on
  sibyl web                  start the local console (127.0.0.1 only)
  sibyl config check         validate configuration and exit
  sibyl config print         print the resolved configuration with provenance
  sibyl init                 write a fully annotated sibyl.toml
  sibyl licence              show the licence state (SIBYL_LICENSE_KEY)

Options
  --repo owner/name           the repository to review; defaults to [repo].slug
  --config <path>             replace the project config layer
  --no-user-config            ignore the user config layer
  --defaults                  built-in defaults only
  -h, --help                  this
`

function renderDiagnostic(d: Diagnostic): string {
  const mark = d.level === "error" ? "⛔" : "⚠️ "
  const where = d.where !== undefined ? ` (${d.where})` : ""
  const hint = d.hint !== undefined ? `\n     ${d.hint}` : ""
  return `${mark} ${d.message}${where}${hint}`
}

interface ParsedArgs {
  readonly command: string[]
  readonly configPath?: string
  readonly repo?: string
  readonly noUserConfig: boolean
  readonly defaultsOnly: boolean
  readonly help: boolean
}

function parseArgs(argv: readonly string[]): ParsedArgs {
  const command: string[] = []
  let configPath: string | undefined
  let repo: string | undefined
  let noUserConfig = false
  let defaultsOnly = false
  let help = false

  for (let i = 0; i < argv.length; i++) {
    const a = argv[i] as string
    if (a === "--config") {
      const next = argv[i + 1]
      if (next === undefined) {
        process.stderr.write("⛔ --config needs a path\n")
        process.exit(EXIT.USAGE)
      }
      configPath = next
      i++
    } else if (a === "--repo") {
      const next = argv[i + 1]
      if (next === undefined || !/^[\w.-]+\/[\w.-]+$/.test(next)) {
        process.stderr.write(`⛔ --repo needs owner/name, got ${JSON.stringify(next ?? "")}\n`)
        process.exit(EXIT.USAGE)
      }
      repo = next
      i++
    } else if (a === "--reason" || a === "--note" || a === "--by" || a === "--confirm") {
      // ⛔ Passed through to `triage` and `post`, which own their own verbs. Consumed here only so
      // the unknown-flag guard below does not reject a flag that IS handled — a silently ignored
      // flag and a wrongly rejected one are both ways of lying about what the command accepts.
      if (argv[i + 1] === undefined) {
        process.stderr.write(`⛔ ${a} needs a value\n`)
        process.exit(EXIT.USAGE)
      }
      command.push(a, argv[i + 1] as string)
      i++
    } else if (a === "--all" || a === "--dry-run" || a === "--json") {
      // ⛔ Passed through to the command that owns it. `--json` is advertised in recheck's own usage
      // and handled by its own parser, but the unknown-flag guard below rejected it first and the
      // command exited 64 — an advertised flag that cannot be reached is worse than no flag at all.
      command.push(a)
    }
    else if (a === "--no-user-config") noUserConfig = true
    else if (a === "--defaults") defaultsOnly = true
    else if (a === "-h" || a === "--help") help = true
    else if (a.startsWith("-")) {
      // ⛔ Same reasoning as an unknown config key: a silently ignored flag is a setting the user
      // believes is in effect.
      process.stderr.write(`⛔ unknown flag ${a}\n\n${USAGE}`)
      process.exit(EXIT.USAGE)
    } else command.push(a)
  }
  return {
    command,
    ...(configPath !== undefined ? { configPath } : {}),
    ...(repo !== undefined ? { repo } : {}),
    noUserConfig,
    defaultsOnly,
    help,
  }
}

export function main(argv: readonly string[] = process.argv.slice(2)): number {
  const args = parseArgs(argv)
  if (args.help || args.command.length === 0) {
    process.stdout.write(USAGE)
    return EXIT.OK
  }

  const loadOpts = {
    ...(args.configPath !== undefined ? { configPath: args.configPath } : {}),
    noUserConfig: args.noUserConfig,
    defaultsOnly: args.defaultsOnly,
  }

  const [cmd, sub] = args.command

  try {
    if (cmd === "config" && (sub === "check" || sub === "print")) {
      const loaded = loadConfig(loadOpts)
      for (const w of loaded.warnings) process.stderr.write(renderDiagnostic(w) + "\n")
      if (sub === "print") {
        process.stdout.write(describeResolution(loaded) + "\n\n")
        process.stdout.write(JSON.stringify(loaded.config, null, 2) + "\n")
      } else {
        const n = loaded.warnings.length
        process.stdout.write(`✅ configuration is valid${n > 0 ? ` (${n} warning${n === 1 ? "" : "s"})` : ""}\n`)
      }
      return EXIT.OK
    }

    if (cmd === "web") {
      const loaded = loadConfig(loadOpts)
      for (const w of loaded.warnings) process.stderr.write(renderDiagnostic(w) + "\n")
      const { startServer } = require("../web/server.js") as typeof import("../web/server.js")
      startServer()
      const { bind, port } = loaded.config.server
      process.stdout.write(`sibyl console on http://${bind}:${port}\n`)
      process.stdout.write(`the queue is read from ${loaded.config.triage.db}\n`)
      // ⛔ A server command must NOT return an exit code — the caller exits on it and kills the
      // listener it just started. Measured: the console printed its URL and was dead before the
      // first request. STAY_ALIVE tells the entry point to leave the process running.
      return STAY_ALIVE
    }

    if (cmd === "review") {
      const loaded = loadConfig(loadOpts)
      for (const w of loaded.warnings) process.stderr.write(renderDiagnostic(w) + "\n")
      const pr = Number(sub)
      if (!Number.isSafeInteger(pr) || pr <= 0) {
        // ⛔ Validated before it reaches the clone, which renders it into a ref path. An unvalidated
        // number becomes `refs/pull/../../heads/main/merge` and fetches something nobody asked for.
        process.stderr.write(`⛔ \`sibyl review\` needs a pull-request number, got ${JSON.stringify(sub ?? "")}\n`)
        return EXIT.USAGE
      }
      const { runReviewCommand } = require("./review.js") as typeof import("./review.js")
      // ⛔ The command is async and the entry point is not. Returning STAY_ALIVE hands the process
      // to the promise: exiting on a synchronous return code here would kill the review mid-call,
      // which is the same defect `web` had.
      void runReviewCommand({
        ...(args.repo !== undefined ? { repo: args.repo } : {}),
        pr,
        config: loaded.config,
      }).then(
        (rc) => process.exit(rc),
        (e: unknown) => {
          const msg = e instanceof Error ? e.message : String(e)
          process.stderr.write(`⛔ ${msg}\n`)
          process.exit(typeof (e as { exitCode?: unknown }).exitCode === "number" ? (e as { exitCode: number }).exitCode : EXIT.UNAVAILABLE)
        },
      )
      return STAY_ALIVE
    }

    if (cmd === "post") {
      const loaded = loadConfig(loadOpts)
      for (const w of loaded.warnings) process.stderr.write(renderDiagnostic(w) + "\n")
      const pr = Number(sub)
      if (!Number.isSafeInteger(pr) || pr <= 0) {
        // ⛔ Validated before it is rendered into an API path, for the same reason `review` validates
        // it before it reaches a ref template.
        process.stderr.write(`⛔ \`sibyl post\` needs a pull-request number, got ${JSON.stringify(sub ?? "")}\n`)
        return EXIT.USAGE
      }
      const { runPostCommand } = require("./post.js") as typeof import("./post.js")
      // ⛔ Same as `review`: the command is async and the entry point is not, so the process is
      // handed to the promise. Exiting on a synchronous return code here would kill the run
      // mid-request — and this is the one command that writes to a shared repository.
      void runPostCommand({
        ...(args.repo !== undefined ? { repo: args.repo } : {}),
        pr,
        config: loaded.config,
        argv: args.command.slice(1),
      }).then(
        (rc) => process.exit(rc),
        (e: unknown) => {
          const msg = e instanceof Error ? e.message : String(e)
          process.stderr.write(`⛔ ${msg}\n`)
          process.exit(typeof (e as { exitCode?: unknown }).exitCode === "number" ? (e as { exitCode: number }).exitCode : EXIT.UNAVAILABLE)
        },
      )
      return STAY_ALIVE
    }

    if (cmd === "recheck") {
      const loaded = loadConfig(loadOpts)
      for (const w of loaded.warnings) process.stderr.write(renderDiagnostic(w) + "\n")
      const { runRecheckCommand } = require("./recheck.js") as typeof import("./recheck.js")
      void runRecheckCommand({
        config: loaded.config,
        argv: args.command.slice(1),
        ...(args.repo !== undefined ? { repo: args.repo } : {}),
      }).then(
        (rc) => process.exit(rc),
        (e: unknown) => {
          process.stderr.write(`⛔ ${e instanceof Error ? e.message : String(e)}\n`)
          process.exit(EXIT.UNAVAILABLE)
        },
      )
      return STAY_ALIVE
    }

    if (cmd === "triage") {
      const loaded = loadConfig(loadOpts)
      for (const w of loaded.warnings) process.stderr.write(renderDiagnostic(w) + "\n")
      const { runTriageCommand } = require("./triage.js") as typeof import("./triage.js")
      void runTriageCommand({ config: loaded.config, argv: args.command.slice(1) }).then(
        (rc) => process.exit(rc),
        (e: unknown) => {
          process.stderr.write(`⛔ ${e instanceof Error ? e.message : String(e)}\n`)
          process.exit(EXIT.UNAVAILABLE)
        },
      )
      return STAY_ALIVE
    }

    if (cmd === "licence" || cmd === "license") {
      void import("../licence/gate.js").then(
        async ({ licenceStatus, formatStatus }) => {
          process.stdout.write(`${formatStatus(await licenceStatus())}\n`)
          process.stdout.write("   Free: public repositories. Licence: private repositories too.\n")
          process.exit(EXIT.OK)
        },
        (e: unknown) => {
          process.stderr.write(`⛔ ${e instanceof Error ? e.message : String(e)}\n`)
          process.exit(EXIT.UNAVAILABLE)
        },
      )
      return STAY_ALIVE
    }

    if (cmd === "init") {
      const { renderAnnotatedToml } = require("./init.js") as typeof import("./init.js")
      process.stdout.write(renderAnnotatedToml())
      return EXIT.OK
    }

    process.stderr.write(`⛔ unknown command ${JSON.stringify(args.command.join(" "))}\n\n${USAGE}`)
    return EXIT.USAGE
  } catch (e) {
    if (e instanceof ConfigError) {
      for (const d of e.diagnostics) process.stderr.write(renderDiagnostic(d) + "\n")
      return e.exitCode
    }
    throw e
  }
}

// Only run when invoked directly, so tests can import `main` without it executing.
if (process.argv[1] !== undefined && import.meta.url.endsWith(process.argv[1].split("/").pop() ?? "")) {
  const rc = main()
  if (rc !== STAY_ALIVE) process.exit(rc)
}
